import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { PageHeader } from '@/components/ui/form-field';
import { Card, Input, Select, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api } from '@/lib/api';
import { AUDIT_ACTION_GROUPS, auditActionLabel } from '@/lib/audit-labels';
import type { AuditItem, Paginated } from '@/lib/types';
import { formatDateTime } from '@/lib/utils';

export function AuditPage() {
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (action) params.set('action', action);
  if (from) params.set('from', new Date(`${from}T00:00:00`).toISOString());
  if (to) params.set('to', new Date(`${to}T23:59:59`).toISOString());

  const { data, isLoading } = useQuery({
    queryKey: ['audit', params.toString()],
    queryFn: () => api<Paginated<AuditItem>>(`/audit?${params}`),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Audit-Log" description="Unveränderliches Protokoll sicherheitsrelevanter Vorgänge" />
      <Card>
        <div className="flex flex-col gap-2 border-b p-3 sm:flex-row">
          <Select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="sm:w-60" aria-label="Bereich">
            <option value="">Alle Bereiche</option>
            {AUDIT_ACTION_GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </Select>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="sm:w-44" aria-label="Von" />
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="sm:w-44" aria-label="Bis" />
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH className="w-8" />
              <TH>Zeitpunkt</TH>
              <TH>Vorgang</TH>
              <TH className="hidden md:table-cell">Ausgelöst von</TH>
              <TH className="hidden lg:table-cell">IP-Adresse</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={5}>
                  <Skeleton className="h-40" />
                </TD>
              </TR>
            )}
            {data?.items.length === 0 && <EmptyRow colSpan={5}>Keine Einträge im gewählten Zeitraum.</EmptyRow>}
            {data?.items.map((a) => {
              const open = expanded === a.id;
              return (
                <Fragment key={a.id}>
                  <TR className="cursor-pointer" onClick={() => setExpanded(open ? null : a.id)}>
                    <TD className="pr-0 text-muted-foreground">{open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}</TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDateTime(a.createdAt)}</TD>
                    <TD>
                      <span className="font-medium">{auditActionLabel(a.action)}</span>
                      <span className="block font-mono text-[11px] text-muted-foreground">{a.action}</span>
                    </TD>
                    <TD className="hidden max-w-xs truncate md:table-cell">{a.actorLabel ?? 'System'}</TD>
                    <TD className="hidden font-mono text-xs text-muted-foreground lg:table-cell">{a.ipAddress ?? '–'}</TD>
                  </TR>
                  {open && (
                    <TR className="bg-muted/30 hover:bg-muted/30">
                      <TD />
                      <TD colSpan={4}>
                        <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
                          <dt className="text-muted-foreground">Objekt</dt>
                          <dd className="font-mono">
                            {a.entityType}
                            {a.entityId ? ` · ${a.entityId}` : ''}
                          </dd>
                          <dt className="text-muted-foreground">Details</dt>
                          <dd>
                            <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-all font-mono">{a.data ? JSON.stringify(a.data, null, 2) : '–'}</pre>
                          </dd>
                        </dl>
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
    </>
  );
}
